import { useSuspenseQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type AdjustmentInvoiceStatsRequestBody =
  paths['/adjustment-invoices/statistics']['post']['requestBody']['content']['application/json'];
export type AdjustmentInvoiceStatsResponse =
  paths['/adjustment-invoices/statistics']['post']['responses']['200']['content']['application/json'];
export type AdjustmentInvoiceStatsSearchFilters = AdjustmentInvoiceStatsRequestBody['search'];

export interface AdjustmentInvoiceStatsParams {
  makerOrganizationUuid: string;
  search?: AdjustmentInvoiceStatsSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchAdjustmentInvoiceStats = async ({
  makerOrganizationUuid,
  search,
  takerMarkets,
}: AdjustmentInvoiceStatsParams): Promise<AdjustmentInvoiceStatsResponse['data']> => {
  const response = await apiClient
    .post(`api/supplier-experience/adjustment-invoices/statistics`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((market) => ({
          marketUuid: market.marketUuid,
          supplierDivisionUuid: market.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies AdjustmentInvoiceStatsRequestBody,
    })
    .json<AdjustmentInvoiceStatsResponse>();

  return response.data;
};

const useAdjustmentInvoiceStats = (params: AdjustmentInvoiceStatsParams) => {
  const { makerOrganizationUuid, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['adjustment-invoice-stats', makerOrganizationUuid, search, ...takerMarketUuidsMap]
      : ['adjustment-invoice-stats-invalid'],
    queryFn: () => (isValid ? fetchAdjustmentInvoiceStats(params) : null),
    select: (data) => {
      return {
        totalAmount: data?.summary.amount ?? 0,
        totalCount: data?.summary.count ?? 0,
      };
    },
  });
};

export default useAdjustmentInvoiceStats;
